import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, AfterViewInit, ViewChildren, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { PaygService } from '../../../services/payg.service';
import { PaygPaymentTransmissionPerformanceModel } from '../../../models/payg/payg-payment-transmission-performance.model';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';

@Component({
    selector: 'payment-transmission-performance-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; }'],
})
export class PaymentTransmissionPerformanceChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    @Input() data: PaygPaymentTransmissionPerformanceModel[];
    public paymentTransmissionPerformance: PaygPaymentTransmissionPerformanceModel[] = [];
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    gradientStroke: any;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number;

    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(
        private paygService: PaygService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId');
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.gradientStroke = this.basechart.first.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
        this.gradientStroke.addColorStop(0, '#668ac1');
        this.gradientStroke.addColorStop(1, '#39547f');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {
        this.paygService.getPaymentTransmissionPerformance(this.clientId, this.siteId, this.timePeriod.toString()).then().then((result) => {
            this.paymentTransmissionPerformance = result;
            this.chartData = this.processData(this.paymentTransmissionPerformance);
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            this.createChart();
        }).catch((reason) => {
            this.hasDataChange.emit(false);
            this.hasLoadingChange.emit(false);
        });
    }

    processData(data: PaygPaymentTransmissionPerformanceModel[]) {

        let graphData = {
            labels: [],
            payments: []
        };

        for (var i = 0; i < data.length; i++) {
            graphData.labels.push(data[i].transmissionTime);
            graphData.payments.push(data[i].payments);
        }

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'line',
            options: {
                legend: {
                    display: false
                },
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3'
                },
                elements: {
                    line: {
                        tension: 0
                    }
                },
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Minutes',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        gridLines: {
                            display: false
                        }
                    }],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Number of Payments',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            barThickness: 8,
                            id: "y-axis-0",
                            position: 'left',
                            gridLines: {
                                display: true
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;
        let data = [] as any;

        datasets.push({
            fill: 'origin',
            data: this.chartData.payments,
            backgroundColor: '#00539f'
        });

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
